import { useCallback, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Button,
  Center,
  ClickAwayListener,
  Column,
  CustomSpinner,
  Pressable,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Text,
} from '~/commonComponents';
import AnimatedCheckCircle from '~/components/shared/AnimatedCheckCircle';
import ConfirmPopup from '~/components/shared/ConfirmPopup';
import { IFavoriteInvite, IJob, ProfessionTypeEnum } from '~/models';
import { useAppSelector } from '~/hooks/useAppSelector';
import { firePopUpViewed, fireInviteSent, type TInvitePopupSource } from '~/posthogs/events';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { ProfessionalHubRedesign } from '~/constants/ff.posthog';
import CloseIcon from '../../images/close-icon.svg';
import {
  Container,
  FavoriteTabPanel,
  Overlay,
  RecentProfessionalTabPanel,
  RecommendedProsTabPanel,
} from './components';
import { ViewSkillsScreen } from './components/ViewSkillsScreen';
import { useInviteProfessional } from './hooks';

interface IProps {
  isOpen: boolean;
  isRefetchJob: boolean;
  confirmButtonText?: string;
  jobId?: string;
  jobIds?: string[];
  defaultProfessionals?: string[];
  profession?: string;
  officeId?: string;
  onClose: () => void;
  onConfirm?: (ids: string[]) => void;
  onTrackEvent?: () => void;
  trackingSource?: TInvitePopupSource;
}

export const InviteProfessionalPopup = ({
  isOpen,
  isRefetchJob,
  confirmButtonText,
  jobId,
  jobIds,
  defaultProfessionals,
  profession,
  officeId,
  onClose,
  onConfirm,
  onTrackEvent,
  trackingSource,
}: IProps) => {
  const isNewDesign = useFeatureFlag(ProfessionalHubRedesign);
  const buttonText = confirmButtonText ?? (isNewDesign ? 'Confirm' : 'Send Invites');
  const [viewSkillsProfessional, setViewSkillsProfessional] = useState<any>(null);

  const {
    activeTab,
    isInviting,
    isLoading,
    isSuccess,
    selectedProfessionals,
    onClosePopup,
    onSelectProfessionals,
    onSentInvitations,
    onTabChange,
    favoriteProfessionals,
    recentProfessionals,
    recommendedProfessionals,
  } = useInviteProfessional({
    isRefetchJob,
    jobId,
    jobIds,
    defaultProfessionals,
    officeId,
    isOpen,
    onClosePopup: onClose,
    filterProfession: profession as ProfessionTypeEnum,
  });

  const sentIds = useAppSelector((state) => {
    if (!isNewDesign) return new Set<string>();
    const invites = (state.job.job as IJob)?.favoriteInvites ?? [];
    return new Set(invites.map((it: IFavoriteInvite) => it?.professional as any as string));
  });

  const allSelectableIds = useMemo(() => {
    if (!isNewDesign) return undefined;
    const ids = new Set<string>();

    favoriteProfessionals?.forEach((p: any) => {
      if (p.id && !sentIds.has(p.id)) ids.add(p.id);
    });
    recentProfessionals?.forEach((p: any) => {
      const id = p.professional?.id ?? p.id;
      if (id && !sentIds.has(id)) ids.add(id);
    });
    recommendedProfessionals?.forEach((p: any) => {
      if (p.id && !sentIds.has(p.id)) ids.add(p.id);
    });

    return [...ids];
  }, [isNewDesign, favoriteProfessionals, recentProfessionals, recommendedProfessionals, sentIds]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && trackingSource) {
      firePopUpViewed({ source: trackingSource });
    }
  }, [isOpen, trackingSource]);

  const handleClosePopup = useCallback(() => {
    setViewSkillsProfessional(null);
    onClosePopup();
  }, [onClosePopup]);

  const handleConfirmProfessionals = useCallback(() => {
    onTrackEvent?.();

    if (onConfirm) {
      onConfirm([...selectedProfessionals]);

      return;
    }

    // Fire invite_sent event when popup actually sends invites
    if (trackingSource) {
      const selectedIds = [...selectedProfessionals];
      const favoriteCount = selectedIds.filter((id) =>
        favoriteProfessionals?.some((p: any) => p.id === id),
      ).length;
      const workedWithCount = selectedIds.filter((id) =>
        recentProfessionals?.some((p: any) => p.id === id || p.professional?.id === id),
      ).length;
      const newToYouCount = Math.max(0, selectedIds.length - favoriteCount - workedWithCount);

      fireInviteSent({
        shiftId: jobId || jobIds?.[0],
        totalInvitesSent: selectedIds.length,
        favoriteInvitesSent: favoriteCount,
        workedWithInvitesSent: workedWithCount,
        newToYouInvitesSent: newToYouCount,
        source: trackingSource,
      });
    }

    onSentInvitations();
  }, [
    onConfirm,
    onTrackEvent,
    selectedProfessionals,
    onSentInvitations,
    trackingSource,
    favoriteProfessionals,
    recentProfessionals,
    jobId,
    jobIds,
  ]);

  if (!isOpen) {
    return null;
  }

  return (
    <>
      {!isSuccess ? (
        <Overlay>
          <ClickAwayListener onClickAway={() => handleClosePopup()}>
            <Container>
              <AnimatePresence mode="wait">
                {isNewDesign && viewSkillsProfessional ? (
                  <motion.div
                    key="view-skills"
                    initial={{ x: '100%', opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    exit={{ x: '100%', opacity: 0 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    style={{
                      display: 'flex',
                      flex: 1,
                      flexDirection: 'column',
                    }}
                  >
                    <ViewSkillsScreen
                      fullName={viewSkillsProfessional.fullName}
                      profession={viewSkillsProfessional.profession}
                      professionCode={viewSkillsProfessional.professionCode}
                      education={viewSkillsProfessional.education}
                      onBack={() => setViewSkillsProfessional(null)}
                    />
                  </motion.div>
                ) : (
                  <motion.div
                    key="invite-list"
                    initial={false}
                    animate={{ x: 0, opacity: 1 }}
                    exit={{ x: '-30%', opacity: 0 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    style={{
                      display: 'flex',
                      flex: 1,
                      flexDirection: 'column',
                    }}
                  >
                    <Pressable onPress={handleClosePopup}>
                      <img
                        src={CloseIcon}
                        style={{
                          cursor: 'pointer',
                          height: 24,
                          position: 'absolute',
                          right: 20,
                          top: 20,
                          width: 24,
                        }}
                        alt="close_icon"
                      />
                    </Pressable>

                    <Column marginBottom={24} marginTop={54}>
                      <Text bold fontSize={18} textAlign="center">
                        Invite Professionals
                      </Text>
                    </Column>

                    {isLoading && (
                      <Center flex={1}>
                        <CustomSpinner isLoading size={32} />
                      </Center>
                    )}

                    {!isLoading && (
                      <>
                        <Tabs
                          initialTab={activeTab}
                          onChangeTab={onTabChange}
                          style={{ flex: 1 }}
                          tabListStyle={{
                            gap: 20,
                            marginBottom: 8,
                            ...(isNewDesign && { marginBottom: 0 }),
                          }}
                          tabPanelStyle={{
                            display: 'flex',
                            flex: 1,
                          }}
                        >
                          <TabList>
                            <Tab
                              title="Favorites"
                              totalCount={favoriteProfessionals?.length}
                              textStyle={isNewDesign ? { fontSize: 18 } : undefined}
                            />
                            <Tab
                              title="Worked With"
                              totalCount={recentProfessionals?.length}
                              textStyle={isNewDesign ? { fontSize: 18 } : undefined}
                            />
                            <Tab
                              title="New to You"
                              totalCount={recommendedProfessionals?.length}
                              textStyle={isNewDesign ? { fontSize: 18 } : undefined}
                            />
                          </TabList>
                          <TabPanel>
                            <FavoriteTabPanel
                              isLoading={isLoading}
                              selectedProfessionals={selectedProfessionals}
                              onSelectProfessionals={onSelectProfessionals}
                              profession={profession}
                              data={favoriteProfessionals ?? []}
                              isNewDesign={isNewDesign}
                              onViewSkills={(pro) => setViewSkillsProfessional(pro)}
                              allSelectableIds={allSelectableIds}
                            />
                            <RecentProfessionalTabPanel
                              isLoading={isLoading}
                              selectedProfessionals={selectedProfessionals}
                              onSelectProfessionals={onSelectProfessionals}
                              profession={profession}
                              data={recentProfessionals ?? []}
                              isNewDesign={isNewDesign}
                              onViewSkills={(pro) => setViewSkillsProfessional(pro)}
                              allSelectableIds={allSelectableIds}
                            />
                            <RecommendedProsTabPanel
                              isLoading={isLoading}
                              selectedProfessionals={selectedProfessionals}
                              onSelectProfessionals={onSelectProfessionals}
                              profession={profession}
                              data={recommendedProfessionals}
                              isNewDesign={isNewDesign}
                              onViewSkills={(pro) => setViewSkillsProfessional(pro)}
                              allSelectableIds={allSelectableIds}
                            />
                          </TabPanel>
                        </Tabs>
                        <Button
                          text={buttonText}
                          disabled={isNewDesign && selectedProfessionals.size === 0}
                          isLoading={isInviting && !isLoading}
                          style={{
                            marginInline: 'auto',
                            width: 152,
                            marginBottom: 32,
                            marginTop: 24,
                          }}
                          onClick={handleConfirmProfessionals}
                        />
                      </>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </Container>
          </ClickAwayListener>
        </Overlay>
      ) : (
        // @ts-expect-error -- ConfirmPopup still written in JavaScript
        <ConfirmPopup
          Icon={() => (
            <div style={{ marginBottom: 20 }}>
              <AnimatedCheckCircle />
            </div>
          )}
          title="Invite(s) Successfully Sent!"
          description="This shift request has been successfully sent to your selected professionals."
          rightButtonAction={handleClosePopup}
          rightButtonText="Close"
        />
      )}
    </>
  );
};
